import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { BASE_API_URL } from '../helpers/Config';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const MyStakingScreen = ({ theme = 'dark' }) => {
    const isDarkMode = theme === 'dark';
    const [stakingRecords, setStakingRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStaking, setSelectedStaking] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const navigate = useNavigate();

    // Theme colors
    const themeColors = {
        dark: {
            background: '#121826',
            surface: '#1E293B',
            surfaceLight: '#2D3748',
            primary: '#8B5CF6',
            primaryHover: '#7C3AED',
            text: '#E5E7EB',
            textSecondary: '#9CA3AF',
            border: '#374151',
            icon: '#9CA3AF',
            shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            success: '#10B981',
            warning: '#F59E0B',
            info: '#3B82F6',
            error: '#EF4444'
        },
        light: {
            background: '#F9FAFB',
            surface: '#FFFFFF',
            surfaceLight: '#F3F4F6',
            primary: '#7C3AED',
            primaryHover: '#6D28D9',
            text: '#1F2937',
            textSecondary: '#6B7280',
            border: '#E5E7EB',
            icon: '#6B7280',
            shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            success: '#059669',
            warning: '#D97706',
            info: '#2563EB',
            error: '#DC2626'
        }
    };

    const colors = isDarkMode ? themeColors.dark : themeColors.light;

    // Calculate overall progress
    const calculateOverallProgress = (records) => {
        if (!records.length) return 0;
        const totalProgress = records.reduce((sum, staking) => {
            return sum + (staking.given_months / staking.total_months);
        }, 0);
        return Math.round((totalProgress / records.length) * 100);
    };

    // Process actual payout data from API
    const getPayouts = (staking) => {
        if (!staking.pauout || !Array.isArray(staking.pauout)) {
            return [];
        }

        // Sort payouts by date in descending order (newest first)
        return staking.pauout
            .map((payout, index) => ({
                id: payout.commision_id || `payout-${index}`,
                month: index + 1,
                amount: parseFloat(payout.amount),
                date: payout.date,
                status: payout.is_claimed ? 'Yes' : 'No',
                isClaimed: payout.is_claimed
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    // Get plan color based on amount
    const getPlanColor = (amount) => {
        const numAmount = parseFloat(amount);
        if (numAmount >= 100 && numAmount <= 1499) return colors.info;
        if (numAmount >= 1500 && numAmount <= 3499) return colors.primary;
        if (numAmount >= 3500 && numAmount <= 6999) return colors.warning;
        if (numAmount >= 7000) return colors.success;
        return colors.textSecondary;
    };

    // Get plan name based on amount
    const getPlanName = (amount) => {
        const numAmount = parseFloat(amount);
        if (numAmount >= 100 && numAmount <= 1499) return 'Lite';
        if (numAmount >= 1500 && numAmount <= 3499) return 'Plus';
        if (numAmount >= 3500 && numAmount <= 6999) return 'Pro';
        if (numAmount >= 7000) return 'Max';
        return 'Unknown';
    };

    // Fetch staking records
    const fetchStakingRecords = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/staking/list`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) {
                setStakingRecords(data.data);
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to fetch staking records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStakingRecords();
    }, []);

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format date without time
    const formatDateOnly = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(value));
    };

    // Calculate progress percentage
    const calculateProgress = (given, total) => {
        return (given / total) * 100;
    };

    // Get status badge
    const getStatusBadge = (staking) => {
        const isMatured = staking.given_months >= staking.total_months;

        if (isMatured) {
            return {
                text: 'Completed',
                color: colors.success,
                bgColor: colors.success + '20'
            };
        } else {
            return {
                text: 'Active',
                color: colors.primary,
                bgColor: colors.primary + '20'
            };
        }
    };

    // Handle staking click
    const handleStakingClick = (staking) => {
        setSelectedStaking(staking);
        setShowDetailsModal(true);
    };

    // Calculate next dispatch text
    const getNextDispatchText = (staking) => {
        if (staking.given_months >= staking.total_months) {
            return 'All payments completed';
        }
        if (staking.next_dispatch) {
            const nextDate = new Date(staking.next_dispatch);
            const now = new Date();
            const diffTime = nextDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays <= 0) {
                return 'Due today';
            } else if (diffDays === 1) {
                return 'Tomorrow';
            } else {
                return `In ${diffDays} days`;
            }
        }
        return 'Processing...';
    };

    // Calculate total received amount from payouts
    const calculateTotalReceived = (payouts) => {
        if (!payouts || !Array.isArray(payouts)) return 0;
        return payouts.reduce((sum, payout) => {
            return sum + (payout.is_claimed ? parseFloat(payout.amount) : 0);
        }, 0);
    };

    // Calculate next payout amount
    const calculateNextPayoutAmount = (staking) => {
        const totalReturn = parseFloat(staking.amount) * 2;
        return totalReturn / staking.total_months;
    };

    // Animation variants
    const pageVariants = {
        initial: { opacity: 0 },
        animate: {
            opacity: 1,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 20
            }
        }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 25
            }
        },
        exit: {
            opacity: 0,
            scale: 0.9,
            transition: { duration: 0.2 }
        }
    };

    // Custom styles
    const styles = `
        .my-staking-container {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            user-select: none;
            max-width: 500px;
            margin: 0 auto;
            -webkit-tap-highlight-color: transparent;
        }
        
        /* Loading State */
        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
        }
        
        .loading-spinner {
            width: 48px;
            height: 48px;
            border: 3px solid ${colors.primary}40;
            border-top-color: ${colors.primary};
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
        }
        
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
        
        /* Empty State */
        .empty-state {
            text-align: center;
            padding: 60px 20px;
        }
        
        .empty-icon {
            font-size: 48px;
            margin-bottom: 20px;
            opacity: 0.5;
        }
        
        /* Staking Card */
        .staking-card {
            background: ${colors.surface};
            border-radius: 16px;
            border: 1px solid ${colors.border};
            padding: 18px;
            margin-bottom: 14px;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
        }
        
        .staking-card:hover {
            transform: translateY(-2px);
            border-color: ${colors.primary}80;
            box-shadow: 0 8px 24px ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
        }
        
        .staking-card:active {
            transform: translateY(0);
        }
        
        .status-badge {
            position: absolute;
            top: 16px;
            right: 16px;
            padding: 4px 10px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.3px;
        }
        
        .plan-badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 700;
            margin-bottom: 10px;
        }
        
        .amount-display {
            font-size: 24px;
            font-weight: 800;
            color: ${colors.text};
            margin-bottom: 4px;
            letter-spacing: -0.5px;
        }
        
        .currency-symbol {
            font-size: 18px;
            color: ${colors.textSecondary};
            margin-right: 2px;
        }
        
        .date-info {
            font-size: 12px;
            color: ${colors.textSecondary};
            margin-bottom: 14px;
        }
        
        /* Progress Bar */
        .progress-container {
            margin: 16px 0 12px 0;
        }
        
        .progress-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 6px;
        }
        
        .progress-label {
            font-size: 12px;
            color: ${colors.textSecondary};
            font-weight: 500;
        }
        
        .progress-percentage {
            font-size: 12px;
            font-weight: 700;
            color: ${colors.text};
        }
        
        .progress-bar {
            height: 6px;
            background: ${colors.border};
            border-radius: 3px;
            overflow: hidden;
            position: relative;
        }
        
        .progress-fill {
            height: 100%;
            border-radius: 3px;
            position: absolute;
            top: 0;
            left: 0;
            transition: width 0.6s ease;
        }
        
        /* Stats Grid */
        .stats-grid {
            display: grid;
            grid-auto-flow: column;   /* children flow horizontally */
            grid-auto-columns: 1fr;   /* equal width */
            gap: 12px;
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid ${colors.border}50;
            overflow-x: auto;
            white-space: nowrap;
        }
        
        .stat-item {
            text-align: center;
        }
        
        .stat-value {
            font-size: 16px;
            font-weight: 700;
            color: ${colors.text};
            margin-bottom: 2px;
        }
        
        .stat-label {
            font-size: 10px;
            color: ${colors.textSecondary};
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        /* Modal Styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
        }
        
        .modal-content {
            background: ${colors.surface};
            border-radius: 20px;
            max-width: 450px;
            width: 100%;
            max-height: 85vh;
            overflow: hidden;
            border: 1px solid ${colors.border};
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
            display: flex;
            flex-direction: column;
        }
        
        .modal-header {
            padding: 24px 24px 16px;
            border-bottom: 1px solid ${colors.border}50;
            position: relative;
        }
        
        .modal-close {
            position: absolute;
            top: 24px;
            right: 24px;
            width: 32px;
            height: 32px;
            border-radius: 10px;
            background: ${colors.surfaceLight};
            border: 1px solid ${colors.border};
            color: ${colors.text};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 18px;
            transition: all 0.2s ease;
        }
        
        .modal-close:hover {
            background: ${colors.primary};
            color: white;
            border-color: ${colors.primary};
        }
        
        .modal-body {
            padding: 24px;
            overflow-y: auto;
            flex: 1;
        }
        
        .modal-section {
            margin-bottom: 24px;
        }
        
        .modal-section-title {
            font-size: 14px;
            font-weight: 700;
            color: ${colors.text};
            margin-bottom: 16px;
            display: flex;
            align-items: center;
        }
        
        .modal-section-icon {
            margin-right: 8px;
            font-size: 16px;
        }
        
        .details-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
        }
        
        .detail-item {
            padding: 12px;
            background: ${colors.surfaceLight};
            border-radius: 12px;
            border: 1px solid ${colors.border}50;
        }
        
        .detail-label {
            font-size: 11px;
            color: ${colors.textSecondary};
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .detail-value {
            font-size: 14px;
            font-weight: 600;
            color: ${colors.text};
        }
        
        /* Payouts Table */
        .payouts-table-container {
            overflow-x: auto;
        }
        
        .payouts-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
        }
        
        .payouts-table th {
            text-align: left;
            padding: 12px 8px;
            font-weight: 600;
            color: ${colors.textSecondary};
            background: ${colors.surfaceLight};
            border-bottom: 1px solid ${colors.border};
            white-space: nowrap;
        }
        
        .payouts-table td {
            padding: 12px 8px;
            color: ${colors.text};
            border-bottom: 1px solid ${colors.border}30;
        }
        
        .payouts-table tr:last-child td {
            border-bottom: none;
        }
        
        .payouts-table tr:hover td {
            background: ${colors.primary}08;
        }
        
        .payout-status {
            padding: 4px 8px;
            border-radius: 8px;
            font-size: 10px;
            font-weight: 700;
            display: inline-block;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .my-staking-container {
                padding: 16px !important;
            }
            
            .staking-card {
                padding: 16px;
            }
            
            .modal-content {
                max-height: 90vh;
            }
            
            .modal-body {
                padding: 20px;
            }
            
            .details-grid {
                grid-template-columns: 1fr;
                gap: 12px;
            }
            
            .stats-grid {
                grid-template-columns: repeat(2, 1fr);
            }
        }
        
        @media (max-width: 380px) {
            .amount-display {
                font-size: 20px;
            }
            
            .stats-grid {
                grid-template-columns: 1fr;
                gap: 8px;
            }
            
            .modal-content {
                border-radius: 16px;
            }
        }
    `;

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            className="my-staking-container"
            style={{
                minHeight: '100vh',
                padding: '20px 16px',
                background: colors.background,
                color: colors.text,
                transition: 'background 0.25s ease, color 0.25s ease'
            }}
        >
            <style>{styles}</style>

            <div className="container-fluid px-0">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-6">
                    <div>
                        <h1 className="mb-1" style={{
                            fontWeight: 800,
                            fontSize: '28px',
                            color: colors.text
                        }}>
                            My Recovery Trade
                        </h1>
                        <p className="mb-0" style={{
                            color: colors.textSecondary,
                            fontSize: '14px',
                            opacity: 0.8
                        }}>
                            View and manage your R-Trade capitals
                        </p>
                    </div>
                    <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background: colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '18px'
                    }}>
                        📊
                    </div>
                </div>

                {/* Summary Stats - Highlighted Section */}
                {stakingRecords.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="mb-6"
                        style={{
                            background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.surface} 100%)`,
                            borderRadius: '20px',
                            border: `2px solid ${colors.primary}30`,
                            padding: '24px 20px',
                            margin: '20px 0',
                            boxShadow: `0 8px 32px ${isDarkMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(124, 58, 237, 0.1)'},
                                        inset 0 1px 0 ${colors.primary}10`
                        }}
                    >
                        <div className="text-center mb-4">
                            <div style={{
                                fontSize: '12px',
                                color: colors.primary,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                marginBottom: '4px'
                            }}>
                                💎 Capital Summary
                            </div>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: 800,
                                color: colors.text,
                                margin: 0
                            }}>
                                Your R-Trade Portfolio
                            </h3>
                        </div>

                        <div className="row">
                            <div className="col-4 text-center">
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    margin: '0 auto 12px',
                                    borderRadius: '16px',
                                    background: `linear-gradient(135deg, ${colors.primary}20, ${colors.primary}05)`,
                                    border: `2px solid ${colors.primary}30`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '22px',
                                    fontWeight: 700,
                                    color: colors.primary
                                }}>
                                    {stakingRecords.length}
                                </div>
                                <div style={{
                                    fontSize: '20px',
                                    fontWeight: 800,
                                    color: colors.primary,
                                    marginBottom: '4px'
                                }}>
                                    {stakingRecords.length}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: colors.textSecondary,
                                    fontWeight: 600,
                                    letterSpacing: '0.3px'
                                }}>
                                    Total R-Trades
                                </div>
                            </div>
                            <div className="col-4 text-center">
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    margin: '0 auto 12px',
                                    borderRadius: '16px',
                                    background: `linear-gradient(135deg, ${colors.success}20, ${colors.success}05)`,
                                    border: `2px solid ${colors.success}30`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '22px',
                                    fontWeight: 700,
                                    color: colors.success
                                }}>
                                    $
                                </div>
                                <div style={{
                                    fontSize: '20px',
                                    fontWeight: 800,
                                    color: colors.success,
                                    marginBottom: '4px'
                                }}>
                                    ${formatCurrency(stakingRecords.reduce((sum, item) => sum + parseFloat(item.amount), 0))}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: colors.textSecondary,
                                    fontWeight: 600,
                                    letterSpacing: '0.3px'
                                }}>
                                    Total Capital
                                </div>
                            </div>
                            <div className="col-4 text-center">
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    margin: '0 auto 12px',
                                    borderRadius: '16px',
                                    background: `linear-gradient(135deg, ${colors.warning}20, ${colors.warning}05)`,
                                    border: `2px solid ${colors.warning}30`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '22px',
                                    fontWeight: 700,
                                    color: colors.warning
                                }}>
                                    ⚡
                                </div>
                                <div style={{
                                    fontSize: '20px',
                                    fontWeight: 800,
                                    color: colors.warning,
                                    marginBottom: '4px'
                                }}>
                                    {stakingRecords.filter(item => item.given_months < item.total_months).length}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: colors.textSecondary,
                                    fontWeight: 600,
                                    letterSpacing: '0.3px'
                                }}>
                                    Active
                                </div>
                            </div>
                        </div>

                        {/* Overall Progress Bar */}
                        <div style={{
                            marginTop: '20px',
                            paddingTop: '16px',
                            borderTop: `1px solid ${colors.border}30`
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '8px'
                            }}>
                                <div style={{
                                    fontSize: '11px',
                                    color: colors.textSecondary,
                                    fontWeight: 600
                                }}>
                                    Overall Progress
                                </div>
                                <div style={{
                                    fontSize: '11px',
                                    color: colors.primary,
                                    fontWeight: 700
                                }}>
                                    {calculateOverallProgress(stakingRecords)}%
                                </div>
                            </div>
                            <div style={{
                                height: '6px',
                                background: colors.border,
                                borderRadius: '3px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${calculateOverallProgress(stakingRecords)}%`,
                                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.primaryHover})`,
                                    borderRadius: '3px',
                                    transition: 'width 0.6s ease'
                                }}></div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Loading State */}
                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <div style={{ color: colors.textSecondary, fontSize: '14px' }}>
                            Loading your stakings...
                        </div>
                    </div>
                ) : stakingRecords.length === 0 ? (
                    /* Empty State */
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="empty-state"
                    >
                        <div className="empty-icon">💎</div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            color: colors.text,
                            marginBottom: '12px'
                        }}>
                            No Stakings Yet
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: colors.textSecondary,
                            marginBottom: '24px',
                            lineHeight: 1.5
                        }}>
                            Start your first staking to begin earning monthly returns
                        </p>
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/new-staking')}
                            style={{
                                padding: '14px 28px',
                                background: colors.primary,
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '12px',
                                fontWeight: 700,
                                fontSize: '15px',
                                cursor: 'pointer'
                            }}
                        >
                            Start Staking
                        </motion.button>
                    </motion.div>
                ) : (
                    /* Staking Records List */
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {stakingRecords.map((staking, index) => {
                            const status = getStatusBadge(staking);
                            const planColor = getPlanColor(staking.amount);
                            const progress = calculateProgress(staking.given_months, staking.total_months);
                            const totalReturn = parseFloat(staking.amount) * 2;
                            const monthlyReturn = totalReturn / staking.total_months;
                            const totalReceived = calculateTotalReceived(staking.pauout);

                            return (
                                <motion.div
                                    key={staking.staking_id}
                                    variants={itemVariants}
                                    custom={index}
                                >
                                    <div
                                        className="staking-card"
                                        onClick={() => handleStakingClick(staking)}
                                    >
                                        {/* Status Badge */}
                                        <div
                                            className="status-badge"
                                            style={{
                                                background: status.bgColor,
                                                color: status.color
                                            }}
                                        >
                                            {status.text}
                                        </div>

                                        {/* Plan Badge */}
                                        <div
                                            className="plan-badge"
                                            style={{
                                                background: planColor + '20',
                                                color: planColor
                                            }}
                                        >
                                            {getPlanName(staking.amount)} Plan • {staking.total_months} months
                                        </div>

                                        {/* Amount */}
                                        <div className="amount-display">
                                            <span className="currency-symbol">$</span>
                                            {formatCurrency(staking.amount)}
                                        </div>

                                        {/* Date */}
                                        <div className="date-info">
                                            Started on {formatDate(staking.create_date)}
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="progress-container">
                                            <div className="progress-header">
                                                <div className="progress-label">
                                                    Progress: {staking.given_months} of {staking.total_months} months
                                                </div>
                                                <div className="progress-percentage">
                                                    {progress.toFixed(1)}%
                                                </div>
                                            </div>
                                            <div className="progress-bar">
                                                <div
                                                    className="progress-fill"
                                                    style={{
                                                        width: `${progress}%`,
                                                        background: planColor
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        {/* Stats Grid */}
                                        <div className="stats-grid">
                                            <div className="stat-item">
                                                <div className="stat-value">
                                                    ${formatCurrency(monthlyReturn)}
                                                </div>
                                                <div className="stat-label">Monthly</div>
                                            </div>
                                            <div className="stat-item">
                                                <div className="stat-value" style={{ color: colors.success }}>
                                                    ${formatCurrency(totalReceived)}
                                                </div>
                                                <div className="stat-label">Recovered</div>
                                            </div>
                                            <div className="stat-item">
                                                <div className="stat-value">
                                                    {staking.given_months < staking.total_months ? getNextDispatchText(staking) : 'Completed'}
                                                </div>
                                                <div className="stat-label">Next Payout</div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                )}
            </div>

            {/* Details Modal */}
            <AnimatePresence>
                {showDetailsModal && selectedStaking && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowDetailsModal(false)}
                    >
                        <motion.div
                            className="modal-content"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2 style={{
                                    fontSize: '20px',
                                    fontWeight: 800,
                                    color: colors.text,
                                    margin: 0
                                }}>
                                    Recovery Trade Details
                                </h2>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowDetailsModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Staking Details */}
                                <div className="modal-section">
                                    <div className="modal-section-title">
                                        <span className="modal-section-icon">📋</span>
                                        Capital Details
                                    </div>
                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <div className="detail-label">Amount</div>
                                            <div className="detail-value" style={{ fontSize: '18px', fontWeight: 800, color: getPlanColor(selectedStaking.amount) }}>
                                                ${formatCurrency(selectedStaking.amount)}
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Plan</div>
                                            <div className="detail-value" style={{ color: getPlanColor(selectedStaking.amount) }}>
                                                {getPlanName(selectedStaking.amount)} ({selectedStaking.total_months} months)
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Start Date</div>
                                            <div className="detail-value">
                                                {formatDate(selectedStaking.create_date)}
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Progress</div>
                                            <div className="detail-value">
                                                {selectedStaking.given_months} / {selectedStaking.total_months} months ({calculateProgress(selectedStaking.given_months, selectedStaking.total_months).toFixed(1)}%)
                                            </div>
                                        </div>
                                        {selectedStaking.next_dispatch && (
                                            <div className="detail-item">
                                                <div className="detail-label">Next Payout</div>
                                                <div className="detail-value">
                                                    {formatDateOnly(selectedStaking.next_dispatch)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Returns Summary */}
                                <div className="modal-section">
                                    <div className="modal-section-title">
                                        <span className="modal-section-icon">💰</span>
                                        Recovery Summary
                                    </div>
                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <div className="detail-label">Total Capital</div>
                                            <div className="detail-value">${formatCurrency(selectedStaking.amount)}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Total Recovery</div>
                                            <div className="detail-value" style={{ color: colors.success, fontWeight: 700 }}>
                                                ${formatCurrency(parseFloat(selectedStaking.amount) * 2)}
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Monthly Recovery</div>
                                            <div className="detail-value">
                                                ${formatCurrency(calculateNextPayoutAmount(selectedStaking))}
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Total Recovered</div>
                                            <div className="detail-value" style={{ color: colors.success, fontWeight: 700 }}>
                                                ${formatCurrency(calculateTotalReceived(selectedStaking.pauout))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Payout History */}
                                <div className="modal-section">
                                    <div className="modal-section-title">
                                        <span className="modal-section-icon">📅</span>
                                        Payout History
                                    </div>
                                    {selectedStaking.pauout && selectedStaking.pauout.length > 0 ? (
                                        <>
                                            <div className="payouts-table-container">
                                                <table className="payouts-table">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Date</th>
                                                            <th>Amount</th>
                                                            <th>Claimed</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {getPayouts(selectedStaking).map((payout, index) => (
                                                            <tr key={payout.id}>
                                                                <td>{index + 1}</td>
                                                                <td>{formatDateOnly(payout.date)}</td>
                                                                <td>${formatCurrency(payout.amount)}</td>
                                                                <td>
                                                                    <span
                                                                        className="payout-status"
                                                                        style={{
                                                                            background: payout.status === 'Yes' ? colors.success + '20' : colors.warning + '20',
                                                                            color: payout.status === 'Yes' ? colors.success : colors.warning
                                                                        }}
                                                                    >
                                                                        {payout.status}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                color: colors.textSecondary,
                                                textAlign: 'center',
                                                marginTop: '12px',
                                                fontStyle: 'italic'
                                            }}>
                                                Showing {selectedStaking.pauout.length} payout(s) • {selectedStaking.given_months} of {selectedStaking.total_months} months completed
                                            </div>
                                        </>
                                    ) : (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '30px 20px',
                                            color: colors.textSecondary,
                                            fontSize: '14px'
                                        }}>
                                            <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}>📋</div>
                                            No payout records yet
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default MyStakingScreen;